import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useLang } from "./LangContext";

function FaqItem({ item, index, isInView }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.1 * index, duration: 0.4 }}
      className="border border-slate-200 rounded-xl overflow-hidden hover:border-[#73B5E2]/30 transition-colors"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="text-sm font-medium text-slate-900 pr-4">{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#73B5E2] flex-shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</p>
      </motion.div>
    </motion.div>
  );
}

export default function FAQ() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  // Inject FAQPage schema.org structured data
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faq.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    script.id = "faq-schema";
    const existing = document.getElementById("faq-schema");
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, [t.faq.items]);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
            {t.faq.label}
          </span>
          <h2
            data-testid="faq-title"
            className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
            style={{ fontFamily: 'Outfit' }}
          >
            {t.faq.title}
          </h2>
        </motion.div>

        <div className="space-y-3">
          {t.faq.items.map((item, i) => (
            <FaqItem key={i} item={item} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
}
